import { Icon, MenuGroupContent, MenuOption } from "@smartsuite/react-ui/lib";

import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as R from "remeda";
import { isTruthy } from "remeda";
import type { DynamicSectionOption } from "../../../../../../../../types/line";
import { StyledIcon, StyledNoFields, StyledOptionLabel } from "./style";
import {
  MENU_MAX_HEIGHT,
  MENU_MAX_HEIGHT_MARGIN,
} from "../../../../../../../../utils/constants";
import { getTimezoneShortLabel } from "../../../../../../../../timezone/timezones";
import { getLabel as getDisabledOptionLabel } from "../../../../../../../../utils/conditions-coverter.hook";
import { Tab } from "../TriggerDropdown/TriggerDropdown";
import { SolutionColorVariables } from "@smartsuite/types";

export const SubMenuOptions: React.FC<{
  title?: string;
  prevTab: Tab;
  onNavigateBack: () => void;
  addPill: (attrs: PillNodeAttrs) => void;
  options: DynamicSectionOption[];
  disabledOptionId?: string[];
  timezone?: string;
  hasSingleMenuItem?: boolean;
  dateOptionsWithTime?: boolean;
  selectedActionHash?: string;
  loopingMenuHash?: string;
}> = ({
  title,
  prevTab,
  onNavigateBack,
  addPill,
  options,
  disabledOptionId,
  hasSingleMenuItem,
  timezone = "",
  selectedActionHash,
  loopingMenuHash,
}) => {
  const dateOptions: JSX.Element[] = [];
  const [menuHeight, setMenuHeight] = useState<number>(MENU_MAX_HEIGHT);

  const getLabelText = useCallback(
    (option: DynamicSectionOption, withSuffix: boolean = false): string => {
      if (withSuffix && option.labelSuffix) {
        return `${option.label}: ${option.labelSuffix}`;
      }

      const isAutoNumberField =
        option.smartsuiteFieldType === "autonumberfield";

      if (option.labelSuffix && !isAutoNumberField) {
        return `${option.label}: ${option.labelSuffix}`;
      }

      return option.label;
    },
    [],
  );

  const getRenderedLabel = useCallback(
    (option: DynamicSectionOption): ReactNode => {
      const labelText = getLabelText(option);
      const isAutoNumberField =
        option.smartsuiteFieldType === "autonumberfield";
      const isAutoNumberEntireField =
        isAutoNumberField && option.labelSuffix === "Entire Field";

      if (
        isAutoNumberEntireField ||
        (!option.labelSuffix && !isAutoNumberField)
      ) {
        return (
          <>
            <span>{labelText}</span>
            <StyledOptionLabel> • Entire Field</StyledOptionLabel>
          </>
        );
      }

      return <span>{labelText}</span>;
    },
    [getLabelText],
  );

  useEffect(() => {
    if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
      setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
    } else {
      setMenuHeight(MENU_MAX_HEIGHT);
    }
    window.addEventListener("resize", handleResize, false);

    return () => {
      window.removeEventListener("resize", handleResize, false);
    };
  }, []);

  const handleResize = (): void => {
    if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
      setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
    } else {
      setMenuHeight(MENU_MAX_HEIGHT);
    }
  };

  const unselectedOptions = options.filter((option) => {
    return R.isTruthy(disabledOptionId)
      ? !disabledOptionId.some((id) => {
          const label = getDisabledOptionLabel(option);
          return id === label;
        })
      : true;
  });

  const optionRef = useRef<HTMLDivElement>(null);

  const getHash = (prevTab: Tab): string | undefined => {
    if (prevTab === Tab.ACTION_FIELDS) {
      return selectedActionHash;
    }
    if (prevTab === Tab.CURRENT_ITEM) {
      return loopingMenuHash;
    }
    return undefined;
  };

  return (
    <div ref={optionRef}>
      <MenuGroupContent
        title={
          isTruthy(title)
            ? title
            : isTruthy(hasSingleMenuItem)
              ? t("field-popups.trigger-popup-single-header")
              : t("field-popups.trigger-popup")
        }
        subtitle={getTimezoneShortLabel(timezone)}
        onNavigateBack={hasSingleMenuItem ? () => {} : onNavigateBack}
        search={false}
        hideBackIcon={isTruthy(hasSingleMenuItem)}
        maxHeight={menuHeight}
      >
        {unselectedOptions
          .sort((a, b) => {
            return getLabelText(a).localeCompare(getLabelText(b));
          })
          .map((option, index) => {
            const label = getLabelText(option, true);

            return (
              <MenuOption
                key={`option-${index}`}
                onClick={() => {
                  addPill({
                    icon: option.icon,
                    title: label,
                    value: JSON.stringify({
                      value: option.outputId,
                      target: [Tab.LOOPING_FIELDS, Tab.ACTION_FIELDS].includes(
                        prevTab,
                      )
                        ? "action"
                        : "trigger",
                      hash: getHash(prevTab),
                    }),
                  });
                }}
              >
                <>
                  <StyledIcon>
                    <Icon
                      name={option.icon}
                      size="S"
                      color={SolutionColorVariables.sc1}
                    />
                  </StyledIcon>
                  {getRenderedLabel(option)}
                </>
              </MenuOption>
            );
          })
          .concat(dateOptions)}
        {unselectedOptions.length === 0 && (
          <StyledNoFields>No results</StyledNoFields>
        )}
      </MenuGroupContent>
    </div>
  );
};
